/**
 * network/PostCard — one social post in the network-style feed.
 * Renders body + optional media (image or link preview) + a reactions bar
 * (like / celebrate / insightful / funny) + a lazy-loaded comments thread.
 *
 * Props:
 *   post   : hydrated post payload from GET /api/feed/posts
 *   onDelete(id) : optional — parent removes from its own list on 200
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  ThumbsUp, PartyPopper, Sparkles, Laugh, MessageCircle, MoreHorizontal,
  Trash2, Send, Loader2, ExternalLink, Share2,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const REACTIONS = [
  { key: 'like',       label: 'Like',       Icon: ThumbsUp,     color: 'text-blue-600'    },
  { key: 'celebrate',  label: 'Celebrate',  Icon: PartyPopper,  color: 'text-fuchsia-600' },
  { key: 'insightful', label: 'Insightful', Icon: Sparkles,     color: 'text-amber-600'   },
  { key: 'funny',      label: 'Funny',      Icon: Laugh,        color: 'text-emerald-600' },
];

const PostCard = ({ post, onDelete }) => {
  const { API, user } = useContext(AuthContext);
  const [state, setState] = useState(post);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setState(post); }, [post]);

  const reactionsTotal = REACTIONS.reduce((sum, r) => sum + (state.reactions_count?.[r.key] || 0), 0);

  const toggleReaction = async (key) => {
    try {
      const { data } = await axios.post(`${API}/feed/posts/${state.id}/react`, { reaction: key });
      setState((s) => ({ ...s, viewer_reaction: data.viewer_reaction, reactions_count: data.reactions_count }));
    } catch { toast.error('Could not react'); }
  };

  const openComments = async () => {
    const willOpen = !showComments;
    setShowComments(willOpen);
    if (willOpen && !commentsLoaded) {
      try {
        const { data } = await axios.get(`${API}/feed/posts/${state.id}/comments`);
        setComments(data?.comments || []);
        setCommentsLoaded(true);
      } catch { toast.error('Could not load comments'); }
    }
  };

  const submitComment = async () => {
    if (!draft.trim() || posting) return;
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/feed/posts/${state.id}/comments`, { body: draft.trim() });
      setComments((c) => [...c, data]);
      setState((s) => ({ ...s, comments_count: (s.comments_count || 0) + 1 }));
      setDraft('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not comment'); }
    finally { setPosting(false); }
  };

  const del = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/feed/posts/${state.id}`);
      toast.success('Post deleted');
      onDelete && onDelete(state.id);
    } catch { toast.error('Delete failed'); }
  };

  const sharePost = async () => {
    const authorKey = state.author?.handle || state.author?.id || '';
    const url = `${window.location.origin}/u/${authorKey}?post=${state.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = url; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast.success('Link copied'); }
      catch { toast.error('Could not copy link'); }
      finally { document.body.removeChild(ta); }
    }
  };

  const isOwner = state.author?.id === user?.id;
  const primary = REACTIONS.find((r) => r.key === state.viewer_reaction);
  const PrimaryIcon = primary?.Icon || ThumbsUp;
  const primaryLabel = primary?.label || 'React';
  const primaryClass = primary ? primary.color : 'text-muted-foreground';

  return (
    <article className="pro-card p-4 sm:p-5" data-testid={`post-card-${state.id}`}>
      {/* Author */}
      <header className="flex items-start gap-3">
        <Link to={`/u/${state.author?.handle || state.author?.id}`} className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-bold overflow-hidden flex-shrink-0">
          {state.author?.avatar_url
            ? <img src={state.author.avatar_url} alt="" className="h-full w-full object-cover" />
            : (state.author?.name || '?').slice(0, 2).toUpperCase()}
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={`/u/${state.author?.handle || state.author?.id}`} className="font-heading text-sm font-extrabold hover:text-primary transition-colors truncate block">
            {state.author?.name || 'Member'}
          </Link>
          {state.author?.headline && (
            <div className="text-[11px] text-muted-foreground truncate">{state.author.headline}</div>
          )}
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-0.5">
            {(() => { try { return formatDistanceToNow(parseISO(state.created_at), { addSuffix: true }); } catch { return ''; } })()}
          </div>
        </div>
        {isOwner && (
          <div className="relative">
            <button
              type="button"
              className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid={`post-menu-${state.id}`}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 w-40 rounded-lg border border-border bg-popover shadow-lg z-10 py-1">
                <button
                  type="button"
                  onClick={del}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/5"
                  data-testid={`post-delete-${state.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete post
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Body */}
      {state.body && (
        <div className="mt-3 whitespace-pre-wrap text-sm text-foreground leading-relaxed break-words">
          {state.body}
        </div>
      )}

      {/* Media */}
      {state.media?.length > 0 && (
        <div className="mt-3 space-y-2">
          {state.media.map((m, i) => (
            m.type === 'image' ? (
              <img
                key={i}
                src={m.url}
                alt={m.title || 'Post image'}
                className="w-full max-h-[420px] object-cover rounded-lg border border-border"
              />
            ) : (
              <a
                key={i}
                href={m.url}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-stretch gap-3 rounded-lg border border-border hover:border-primary/40 transition-colors overflow-hidden bg-muted/30"
              >
                {m.thumbnail && (
                  <img src={m.thumbnail} alt="" className="w-28 h-24 object-cover flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1 py-2 pr-3">
                  {m.site_name && <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground truncate">{m.site_name}</div>}
                  {m.title && <div className="font-heading font-extrabold text-sm truncate">{m.title}</div>}
                  {m.description && <div className="text-xs text-muted-foreground line-clamp-2">{m.description}</div>}
                  <div className="text-[10px] text-primary inline-flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> {new URL(m.url).host}</div>
                </div>
              </a>
            )
          ))}
        </div>
      )}

      {/* Counts row */}
      {(reactionsTotal > 0 || state.comments_count > 0) && (
        <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground border-b border-border pb-2">
          <div className="flex items-center gap-1" data-testid={`post-reactions-summary-${state.id}`}>
            {REACTIONS.filter((r) => (state.reactions_count?.[r.key] || 0) > 0).slice(0, 3).map((r) => (
              <r.Icon key={r.key} className={`w-3 h-3 ${r.color}`} />
            ))}
            {reactionsTotal > 0 && <span className="ml-1">{reactionsTotal}</span>}
          </div>
          {state.comments_count > 0 && (
            <button type="button" onClick={openComments} className="hover:underline">
              {state.comments_count} comment{state.comments_count === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-2 flex items-center gap-1">
        <div className="relative group">
          <button
            type="button"
            onClick={() => toggleReaction(state.viewer_reaction || 'like')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-muted transition-colors ${primaryClass}`}
            data-testid={`post-react-btn-${state.id}`}
          >
            <PrimaryIcon className={`w-4 h-4 ${state.viewer_reaction ? 'fill-current' : ''}`} /> {primaryLabel}
          </button>
          {/* Reaction picker on hover */}
          <div className="absolute bottom-full left-0 mb-1 flex items-center gap-1 rounded-full border border-border bg-popover shadow-lg p-1 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity">
            {REACTIONS.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => toggleReaction(r.key)}
                title={r.label}
                data-testid={`react-${r.key}-${state.id}`}
                className={`h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center ${r.color} hover:scale-125 transition-transform`}
              >
                <r.Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={openComments}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-muted text-muted-foreground transition-colors"
          data-testid={`post-comments-btn-${state.id}`}
        >
          <MessageCircle className="w-4 h-4" /> Comment
        </button>
        <button
          type="button"
          onClick={sharePost}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-muted text-muted-foreground transition-colors"
          data-testid={`post-share-btn-${state.id}`}
        >
          <Share2 className="w-4 h-4" /> Send
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-3 border-t border-border pt-3 space-y-3" data-testid={`post-comments-${state.id}`}>
          {commentsLoaded ? (
            comments.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">Be the first to comment.</div>
            ) : (
              comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2" data-testid={`comment-${c.id}`}>
                  <Link to={`/u/${c.author?.handle || c.author?.id}`} className="h-7 w-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                    {c.author?.avatar_url
                      ? <img src={c.author.avatar_url} alt="" className="h-full w-full object-cover" />
                      : (c.author?.name || '?').slice(0, 2).toUpperCase()}
                  </Link>
                  <div className="min-w-0 flex-1 rounded-lg bg-muted/60 px-3 py-2">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-xs truncate">{c.author?.name || 'Member'}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {(() => { try { return formatDistanceToNow(parseISO(c.created_at), { addSuffix: true }); } catch { return ''; } })()}
                      </span>
                    </div>
                    <div className="text-sm text-foreground whitespace-pre-wrap break-words">{c.body}</div>
                  </div>
                </div>
              ))
            )
          ) : (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> loading…</div>
          )}
          <div className="flex items-start gap-2">
            <div className="h-7 w-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
              {user?.avatar_url
                ? <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
                : (user?.name || user?.email || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <Textarea
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                placeholder="Write a comment…"
                data-testid={`comment-input-${state.id}`}
                className="text-sm min-h-[36px]"
              />
              {draft.trim() && (
                <div className="flex justify-end mt-1.5">
                  <Button
                    size="sm"
                    onClick={submitComment}
                    disabled={posting}
                    className="rounded-full h-7 text-xs font-bold"
                    data-testid={`comment-submit-${state.id}`}
                  >
                    {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Post</>}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default PostCard;
